import { useState } from 'react'
import { PageHeader, StatusBadge, btnPrimary, colors } from '../../components/ui'
import { useAuth } from '../../hooks/use-auth'
import { useApiList } from '../../hooks/use-api-list'
import { classifyDeviceBadge, relativeTime, type DeviceBadgeHealth } from './fleet-helpers'
import { DeviceDetailModal } from './DeviceDetailModal'
import type { UserRole } from '@alpha-edge/shared'

interface DeviceSnapshot {
  readonly id: string
  readonly organizationId: string
  readonly organizationName: string | null
  readonly organizationIsArchived: boolean | null
  readonly tenancyId: string | null
  readonly tenancyName: string | null
  readonly siteId: string | null
  readonly siteName: string | null
  readonly deviceId: number
  readonly instanceSeq: number
  readonly deviceName: string
  readonly deviceType: string
  readonly protocol: string
  readonly status: string
  readonly currentConfigVersion: number | null
  readonly udmVersion: string | null
  readonly startedAt: string | null
  readonly lastResultAt: string | null
  readonly errorCountLast24h: number
  readonly lastHeartbeatAt: string
  readonly source: string
  readonly createdAt: string
  readonly updatedAt: string
  readonly configDriftDetectedAt: string | null
  readonly restartState: string | null
  readonly pendingRestartConfigVersion: number | null
}

// Filter chip values are the literal UDM status strings the server stores on
// each heartbeat snapshot (see UnifiedDeviceManagerService in the
// Alpha-Instruments repo). Sending them verbatim makes the server-side
// equality filter actually match -- the prior 'active'/'inactive' chips
// returned zero rows because UDM never emits those strings.
type DeviceStatusFilter = 'all' | 'connected' | 'disconnected' | 'disabled'

const STATUS_FILTER_LABELS: Record<DeviceStatusFilter, string> = {
  all: 'All',
  connected: 'Active',
  disconnected: 'Offline',
  disabled: 'Disabled',
}

const BADGE_COLORS: Record<DeviceBadgeHealth, { bg: string; text: string; label: string }> = {
  active: { bg: '#d1fae5', text: '#065f46', label: 'Active' },
  stale: { bg: '#fef3c7', text: '#92400e', label: 'Stale' },
  offline: { bg: '#fee2e2', text: '#991b1b', label: 'Offline' },
  disabled: { bg: '#e5e7eb', text: '#374151', label: 'Disabled' },
}

function HeartbeatBadge({ lastHeartbeatAt, status }: { lastHeartbeatAt: string; status: string }) {
  const health = classifyDeviceBadge(lastHeartbeatAt, status)
  const { bg, text, label } = BADGE_COLORS[health]
  // Surface the raw UDM status string on hover so operators can confirm
  // the host config without opening the detail modal.
  return (
    <span
      title={status}
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '12px',
        background: bg,
        color: text,
        fontSize: '11px',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  )
}

// Shorten a UUID for display: first 8 chars is enough to distinguish tenancies
// in a list. Full id is available in device detail modal.
function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id
}

interface DeviceRowProps {
  readonly device: DeviceSnapshot
  readonly onClick: (d: DeviceSnapshot) => void
}

// Grid `fr` tracks default to `min-width: auto`, so an unbreakable cell value
// (e.g. a long UDM build version like `0.30.0d17+build.abc1234`) widens its
// track and pushes every other column out of alignment with the header, which
// is a separate grid with no such content. `minWidth: 0` + clip keeps each
// track at its proportional share so rows and header stay aligned (#210).
const cellStyle: React.CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

// UDM versions carry a build-metadata suffix after `+`. Show only the release
// portion in the dense table; the full string is in the detail modal (and on
// hover via title).
function shortVersion(v: string): string {
  return v.split('+')[0]
}

function DeviceRow({ device, onClick }: DeviceRowProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(device)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick(device)
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: '1.5fr 0.75fr 0.75fr 0.75fr 0.75fr 1fr 0.75fr 0.75fr',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.45rem 0.75rem',
        borderTop: `1px solid ${colors.tableBorder}`,
        cursor: 'pointer',
        fontSize: '0.875rem',
        color: colors.text,
      }}
      onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.background = colors.tableRowHover)}
      onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.background = 'transparent')}
    >
      <span style={{ ...cellStyle, fontWeight: 500 }} title={device.deviceName}>
        {device.deviceName}
      </span>
      <span style={cellStyle} title={device.deviceType}>
        {device.deviceType}
      </span>
      <span style={cellStyle}>{device.protocol || '—'}</span>
      <span style={cellStyle}>
        <HeartbeatBadge lastHeartbeatAt={device.lastHeartbeatAt} status={device.status} />
      </span>
      <span style={cellStyle}>{device.currentConfigVersion != null ? `v${device.currentConfigVersion}` : '—'}</span>
      <span style={cellStyle} title={device.udmVersion ?? undefined}>
        {device.udmVersion ? shortVersion(device.udmVersion) : '—'}
      </span>
      <span style={cellStyle}>{relativeTime(device.lastHeartbeatAt)}</span>
      <span style={{ ...cellStyle, display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        {device.configDriftDetectedAt ? (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '12px',
              background: '#fef3c7',
              color: '#92400e',
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            Drift
          </span>
        ) : null}
        {device.restartState === 'pending' ? (
          <span
            title={
              device.pendingRestartConfigVersion != null ? `Config v${device.pendingRestartConfigVersion}` : undefined
            }
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '12px',
              background: '#ede9fe',
              color: '#6d28d9',
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            Restart pending{device.pendingRestartConfigVersion != null ? ` v${device.pendingRestartConfigVersion}` : ''}
          </span>
        ) : null}
        {!device.configDriftDetectedAt && device.restartState !== 'pending' && (
          <span style={{ color: colors.textMuted, fontSize: '11px' }}>—</span>
        )}
      </span>
    </div>
  )
}

const DEVICE_TABLE_HEADERS = [
  'Device Name',
  'Type',
  'Protocol',
  'Status',
  'Config Ver',
  'UDM Version',
  'Last Heartbeat',
  'Config',
]

function DeviceTableHeader() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1.5fr 0.75fr 0.75fr 0.75fr 0.75fr 1fr 0.75fr 0.75fr',
        gap: '0.5rem',
        padding: '0.35rem 0.75rem',
        background: colors.tableHeaderBg,
        borderBottom: `1px solid ${colors.tableBorder}`,
      }}
    >
      {DEVICE_TABLE_HEADERS.map((h) => (
        <span
          key={h}
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
          }}
        >
          {h}
        </span>
      ))}
    </div>
  )
}

interface SiteSectionProps {
  readonly siteName: string | null
  readonly devices: readonly DeviceSnapshot[]
  readonly onDeviceClick: (d: DeviceSnapshot) => void
}

function SiteSection({ siteName, devices, onDeviceClick }: SiteSectionProps) {
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.35rem 0.75rem',
          background: '#f0f4f8',
          borderBottom: `1px solid ${colors.tableBorder}`,
        }}
      >
        <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: colors.textHeading }}>
          {siteName ?? 'Unassigned'}
        </span>
        <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>
          {devices.length} {devices.length === 1 ? 'device' : 'devices'}
        </span>
      </div>
      <DeviceTableHeader />
      {devices.map((d) => (
        <DeviceRow key={d.id} device={d} onClick={onDeviceClick} />
      ))}
    </div>
  )
}

interface TenancySectionProps {
  readonly tenancyId: string | null
  readonly tenancyName: string | null
  readonly devices: readonly DeviceSnapshot[]
  readonly onDeviceClick: (d: DeviceSnapshot) => void
}

function TenancySection({ tenancyId, tenancyName, devices, onDeviceClick }: TenancySectionProps) {
  // Group by site within this tenancy bucket.
  const siteMap = new Map<string, DeviceSnapshot[]>()
  for (const d of devices) {
    const key = d.siteName ?? '\x00unassigned'
    const existing = siteMap.get(key)
    if (existing) {
      existing.push(d)
    } else {
      siteMap.set(key, [d])
    }
  }
  // Sort site keys: real names first, then the unassigned sentinel.
  const siteKeys = [...siteMap.keys()].sort((a, b) => {
    if (a === '\x00unassigned') return 1
    if (b === '\x00unassigned') return -1
    return a.localeCompare(b)
  })

  const label =
    tenancyId === null ? 'Independent / Direct to Central' : (tenancyName ?? `Tenancy ${shortId(tenancyId)}`)

  const labelColor = tenancyId === null ? colors.textMuted : colors.accent

  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: '0.375rem',
        overflow: 'hidden',
        marginBottom: '0.75rem',
      }}
    >
      <div
        style={{
          padding: '0.5rem 0.75rem',
          background: colors.bgMuted,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: labelColor }}>{label}</span>
        {tenancyId !== null && (
          <span
            style={{
              fontSize: '0.75rem',
              fontFamily: 'monospace',
              color: colors.textMuted,
              background: '#e5e7eb',
              padding: '1px 6px',
              borderRadius: '3px',
            }}
          >
            {tenancyId}
          </span>
        )}
      </div>
      {siteKeys.map((siteKey) => {
        const siteDevices = siteMap.get(siteKey) ?? []
        const displayName = siteKey === '\x00unassigned' ? null : siteKey
        return <SiteSection key={siteKey} siteName={displayName} devices={siteDevices} onDeviceClick={onDeviceClick} />
      })}
    </div>
  )
}

// Group a flat list of device snapshots into the Org -> Tenancy/Independent -> Site hierarchy.
function groupDevices(devices: readonly DeviceSnapshot[]): Map<
  string,
  {
    orgName: string | null
    orgId: string
    orgIsArchived: boolean
    tenancies: Map<string | null, { tenancyName: string | null; devices: DeviceSnapshot[] }>
  }
> {
  const orgMap = new Map<
    string,
    {
      orgName: string | null
      orgId: string
      orgIsArchived: boolean
      tenancies: Map<string | null, { tenancyName: string | null; devices: DeviceSnapshot[] }>
    }
  >()
  for (const d of devices) {
    let org = orgMap.get(d.organizationId)
    if (!org) {
      org = {
        orgName: d.organizationName,
        orgId: d.organizationId,
        orgIsArchived: d.organizationIsArchived ?? false,
        tenancies: new Map(),
      }
      orgMap.set(d.organizationId, org)
    }
    const tKey = d.tenancyId
    const existing = org.tenancies.get(tKey)
    if (existing) {
      existing.devices.push(d)
    } else {
      org.tenancies.set(tKey, { tenancyName: d.tenancyName, devices: [d] })
    }
  }
  return orgMap
}

export function FleetDevicesPage() {
  const { user } = useAuth()
  const role = user?.role as UserRole | undefined
  const isMedflow = role === 'medflow_admin'

  const [statusFilter, setStatusFilter] = useState<DeviceStatusFilter>('all')
  const [search, setSearch] = useState('')
  const [selectedDevice, setSelectedDevice] = useState<DeviceSnapshot | null>(null)

  const {
    data: allDevices,
    loading,
    error,
    refresh,
  } = useApiList<DeviceSnapshot>({
    path: '/fleet/devices',
    limit: 100,
    filters: statusFilter !== 'all' ? { status: statusFilter } : {},
  })

  const filteredDevices = search
    ? allDevices.filter((d) => d.deviceName.toLowerCase().includes(search.toLowerCase()))
    : allDevices

  const filterStyle = (f: DeviceStatusFilter): React.CSSProperties => ({
    padding: '0.35rem 0.75rem',
    border: `1px solid ${statusFilter === f ? colors.accent : colors.border}`,
    borderRadius: '4px',
    background: statusFilter === f ? '#eef4ff' : '#fff',
    color: statusFilter === f ? colors.accent : colors.text,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
  })

  const grouped = groupDevices(filteredDevices)
  // Sort orgs: named orgs first (alpha), then fallback-to-id orgs.
  const orgEntries = [...grouped.entries()].sort(([, a], [, b]) => {
    const aName = a.orgName ?? ''
    const bName = b.orgName ?? ''
    return aName.localeCompare(bName)
  })

  return (
    <div>
      <PageHeader
        title="Device Status"
        subtitle={`${filteredDevices.length} device${filteredDevices.length !== 1 ? 's' : ''}`}
        actions={
          <button
            type="button"
            onClick={refresh}
            style={{ ...btnPrimary, background: '#fff', color: colors.accent, border: `1px solid ${colors.accent}` }}
          >
            Refresh
          </button>
        }
      />

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {(['all', 'connected', 'disabled', 'disconnected'] as const).map((f) => (
            <button key={f} type="button" onClick={() => setStatusFilter(f)} style={filterStyle(f)}>
              {STATUS_FILTER_LABELS[f]}
            </button>
          ))}
        </div>

        <input
          type="text"
          placeholder="Search by device name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '0.35rem 0.6rem',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            fontSize: '13px',
            minWidth: '200px',
          }}
        />
      </div>

      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : filteredDevices.length === 0 ? (
        <div style={{ color: colors.textMuted, padding: '1.5rem 0' }}>No devices found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {orgEntries.map(([orgId, orgData]) => (
            <div key={orgId}>
              {/* Org header — only shown for medflow_admin where multiple orgs appear */}
              {isMedflow && (
                <div
                  style={{
                    padding: '0.5rem 0',
                    marginBottom: '0.5rem',
                    borderBottom: `2px solid ${colors.border}`,
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: colors.textHeading }}>
                    {orgData.orgName ?? (
                      <span style={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: colors.textMuted }}>
                        {orgId}
                      </span>
                    )}{' '}
                    {orgData.orgIsArchived ? <StatusBadge status="inactive" label="archived" /> : null}
                  </span>
                </div>
              )}

              {/* Tenancy buckets: tenancyId=null first (independent), then tenancies sorted by id */}
              {[...orgData.tenancies.entries()]
                .sort(([a], [b]) => {
                  if (a === null) return 1
                  if (b === null) return -1
                  return a.localeCompare(b)
                })
                .map(([tenancyId, { tenancyName, devices: tenancyDevices }]) => (
                  <TenancySection
                    key={tenancyId ?? '__independent__'}
                    tenancyId={tenancyId}
                    tenancyName={tenancyName}
                    devices={tenancyDevices}
                    onDeviceClick={setSelectedDevice}
                  />
                ))}
            </div>
          ))}
        </div>
      )}

      {selectedDevice && (
        <DeviceDetailModal
          device={selectedDevice}
          isOpen={!!selectedDevice}
          onClose={() => setSelectedDevice(null)}
          isMedflowAdmin={isMedflow}
        />
      )}
    </div>
  )
}
